/**
 *
 * @module mediaItem
 *
 */

import Check from 'check-types';
import Services from './../services/entry';
import MediaDescriptor from './mediaDescriptor';
import Playlist from './playlist';
import PlaybackAttributes from './playbackAttributes';
import PlaylistType from './playlistType';
import MediaAnalyticsKey from './mediaAnalyticsKey';

const Util = Services.Util;
const MediaPayload = Services.Media.MediaPayload;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    includes,
    instanceStrict
} = Check.assert;

/**
 *
 * @class MediaItem
 * @since 2.0.0
 * @desc Encapsulated payload and media description with convenience methods
 * for retrieving streams and telemetry data.
 *
 */
export default class MediaItem {

    /**
     *
     * @param {SDK.Services.Media.MediaPayload} mediaPayload
     * @param {MediaDescriptor} mediaDescriptor
     * @throws {InvalidArgumentException}
     *
     */
    constructor(mediaPayload, mediaDescriptor) {

        const errorMessage = {
            ctor: 'MediaItem(mediaPayload, mediaDescriptor)',
            get mediaPayload() {
                return `${this.ctor} needs SDK.Services.Media.MediaPayload instance`;
            },
            get mediaDescriptor() {
                return `${this.ctor} needs MediaDescriptor instance`;
            }
        };

        instanceStrict(mediaPayload, MediaPayload, errorMessage.mediaPayload, InvalidArgumentException);
        instanceStrict(mediaDescriptor, MediaDescriptor, errorMessage.mediaDescriptor, InvalidArgumentException);

        /**
         *
         * @access private
         * @type {SDK.Services.Media.MediaPayload}
         *
         */
        this.payload = mediaPayload;

        /**
         *
         * @access private
         * @type {MediaDescriptor}
         *
         */
        this.descriptor = mediaDescriptor;

        /**
         *
         * @access public
         * @type {MediaPlayhead}
         *
         */
        this.playhead = this.payload.playhead;

    }

    /**
     *
     * @access public
     * @returns {Promise<Playlist>}
     *
     */
    getDefaultPlaylist() {

        const { payload } = this;
        const { attributes = {} } = payload;
        const { adPartner, drms, encryptionType, audioSegmentTypes, videoSegmentTypes } = attributes;

        const availablePlaylistTypes = this.getAvailablePlaylistTypes(payload);
        const defaultPlaylistType = [...availablePlaylistTypes].shift();
        const playlistType = this.getPlaylistType(defaultPlaylistType, availablePlaylistTypes);
        const streamUri = this.getStreamUri(defaultPlaylistType, payload);

        return Promise.resolve(
            new Playlist(
                streamUri,
                playlistType,
                availablePlaylistTypes,
                new PlaybackAttributes(
                    adPartner,
                    drms,
                    encryptionType,
                    audioSegmentTypes,
                    videoSegmentTypes
                )
            )
        );

    }

    /**
     *
     * @access public
     * @param {PlaylistType} playlistType
     * @returns {Promise<Playlist>}
     *
     */
    getPreferredPlaylist(playlistType) {

        const errorMessage = {
            method: 'MediaItem.getPreferredPlaylist(playlistType)',
            get playlistType() {
                return `${this.method} needs a valid PlaylistType`;
            }
        };

        includes(PlaylistType, playlistType, errorMessage.playlistType, InvalidArgumentException);

        const { payload } = this;
        const { attributes = {} } = payload;
        const { adPartner, drms, encryptionType, audioSegmentTypes, videoSegmentTypes } = attributes;

        const availablePlaylistTypes = this.getAvailablePlaylistTypes(payload);
        const preferredPlaylistType = this.getPlaylistType(playlistType, availablePlaylistTypes);
        const streamUri = this.getStreamUri(preferredPlaylistType, payload);

        return Promise.resolve(
            new Playlist(
                streamUri,
                preferredPlaylistType,
                availablePlaylistTypes,
                new PlaybackAttributes(
                    adPartner,
                    drms,
                    encryptionType,
                    audioSegmentTypes,
                    videoSegmentTypes
                )
            )
        );

    }

    /**
     *
     * @access public
     * @param {MediaAnalyticsKey} [key=MediaAnalyticsKey.Conviva]
     * @returns {Promise<Object>}
     * @example mediaItem.getTrackingData(MediaAnalyticsKey.Conviva).then((trackingData) => {});
     *
     */
    getTrackingData(key = MediaAnalyticsKey.Conviva) {

        const errorMessage = {
            method: 'MediaItem.getTrackingData(key)',
            get key() {
                return `${this.method} needs a valid MediaAnalyticsKey instance`;
            }
        };

        includes(MediaAnalyticsKey, key, errorMessage.key, InvalidArgumentException);

        const keyClean = key.toLowerCase();
        const payload = this.payload;
        const trackingInfo = payload.trackingInfo;
        const trackingData = trackingInfo[keyClean] || {};

        return Promise.resolve(trackingData);

    }

    /**
     *
     * @access public
     * @param {PlaylistType} [playlistType=PlaylistType.COMPLETE]
     * @returns {Promise<Object>}
     *
     */
    getSegmentTypes(playlistType = PlaylistType.COMPLETE) {

        return this.getPreferredPlaylist(playlistType).then((playlist) => {

            return Promise.resolve({
                videoSegmentTypes: Util.getSafe(() => playlist.attributes.videoSegmentTypes),
                audioSegmentTypes: Util.getSafe(() => playlist.attributes.audioSegmentTypes)
            });

        });

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {PlaylistType} playlistType
     * @param {SDK.Services.Media.MediaPayload} payload
     * @returns {String}
     *
     */
    getStreamUri(playlistType, payload) {

        const { COMPLETE } = PlaylistType;
        const { completeStreamUri, slideStreamUri } = payload;

        const streamUri = playlistType === COMPLETE ? completeStreamUri : slideStreamUri;

        return streamUri;

    }

    /**
     *
     * @access private
     * @param {SDK.Services.Media.MediaPayload} payload
     * @returns {Array<PlaylistType>}
     *
     */
    getAvailablePlaylistTypes(payload) {

        const { COMPLETE, SLIDE } = PlaylistType;
        const { completeStreamUri, slideStreamUri } = payload;

        const availablePlaylistTypes = [];

        if (completeStreamUri && completeStreamUri.trim() !== '') {
            availablePlaylistTypes[availablePlaylistTypes.length] = COMPLETE;
        }

        if (slideStreamUri && slideStreamUri.trim() !== '') {
            availablePlaylistTypes[availablePlaylistTypes.length] = SLIDE;
        }

        return availablePlaylistTypes;

    }

    /**
     *
     * @access private
     * @param {PlaylistType} playlistType
     * @param {Array<PlaylistType>} availablePlaylistTypes
     * @returns {PlaylistType}
     *
     */
    getPlaylistType(playlistType, availablePlaylistTypes) {

        const { COMPLETE, SLIDE } = PlaylistType;
        let availablePlaylistType;

        switch (playlistType) {
            case COMPLETE:
                availablePlaylistType = availablePlaylistTypes.includes(COMPLETE) ? COMPLETE : SLIDE;
                break;
            case SLIDE:
                availablePlaylistType = availablePlaylistTypes.includes(SLIDE) ? SLIDE : COMPLETE;
                break;
            default:
                throw new Error('Unsupported PlaylistType');
        }

        return availablePlaylistType;

    }

}



// WEBPACK FOOTER //
// ./src/media/mediaItem.js